import './CreateWorkspaceModal.scss';
import { useTranslator } from '@Localization';
import { Button } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { ButtonWithLoader } from '../../../../design/buttons/ButtonWithLoader';
import { S_ErrorFeedback } from '../../../../design/feedback/errorFeedback/S_ErrorFeedback';
import { S_Checkbox } from '../../../../design/inputs/checkbox/S_Checkbox';
import { Select } from '../../../../design/inputs/select/Select';
import { S_Modal } from '../../../../design/modals/modal/S_Modal';
import { ValidatedTextInput } from '../../../components/ValidatedTextInput';
import { InputStatus } from '../../../models/error.model';
import { timeZones } from '../../../models/timeZones/timeZones.model';
import { Plan } from '../../user/user.store';

interface ICreateWorkspaceModalProps {
  showCreateWorkspace: boolean;
  newWorkspaceName: string;
  newWorkspaceNameStatus: InputStatus;
  newWorkspaceEncryption: boolean;
  newWorkspaceTimeZone: number;
  canAddWorkspace: boolean;
  plan: Plan;
  onCloseModal: () => void;
  onCreateNameChange: (value: string) => void;
  onEncryptionChange: () => void;
  onTimeZoneChanged: (newTimeZone: number) => void;
  onSubmitCreate: () => Promise<void>;
}

export const CreateWorkspaceModal = observer((props: ICreateWorkspaceModalProps): ReactElement => {
  const { t } = useTranslator();

  const handleTimeZoneChanged = (newValue: number): void => {
    props.onTimeZoneChanged(newValue);
  };

  return (
    <S_Modal
      isOpen={props.showCreateWorkspace}
      toggle={props.onCloseModal}
      headerText={t('create_new_workspace')}
      buttons={
        <>
          <Button
            color='neutral'
            variant='outlined'
            onClick={props.onCloseModal}
          >
            {t('cancel')}
          </Button>
          <ButtonWithLoader
            loaderKey='create-workspace-button'
            id='create-workspace'
            disabled={props.newWorkspaceNameStatus.isValid !== true || !props.canAddWorkspace}
            onClick={props.onSubmitCreate}
          >
            {t('create_workspace')}
          </ButtonWithLoader>
        </>
      }
    >
      <div className='create-workspace-modal'>
        <ValidatedTextInput
          id='workspace-name'
          labelText={t('workspace_name')}
          placeholder={t('workspace_type_new_workspace_name_here')}
          value={props.newWorkspaceName}
          onChange={props.onCreateNameChange}
          onPressEnter={() => {
            void props.onSubmitCreate();
          }}
          isValid={props.newWorkspaceNameStatus.isValid}
          maxLength={200}
          feedbacks={props.newWorkspaceNameStatus.errorCodes}
        />
        <Select
          onChange={(_, v) => {
            handleTimeZoneChanged(v.value ?? timeZones[0].value);
          }}
          value={timeZones.find((x) => x.value === props.newWorkspaceTimeZone)}
          options={timeZones}
        />
        <S_Checkbox
          id='encryption'
          checked={props.newWorkspaceEncryption}
          onChange={props.onEncryptionChange}
          label={t('workspace_enable_encryption')}
        />
        {!props.canAddWorkspace && <S_ErrorFeedback className='darker'>{t('you_have_reached_workspaces_limit', { plan: t(props.plan) })}</S_ErrorFeedback>}
      </div>
    </S_Modal>
  );
});
